import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { useCreateStaffMember } from './useStaffMemberMutations';

type StaffKind = 'TEACHER' | 'AIDE' | 'ADMIN' | 'OTHER';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolId: string;
  defaultKind?: StaffKind;
  onComplete: () => void;
}

export default function CreateStaffMemberDialog({
  open,
  onOpenChange,
  schoolId,
  defaultKind = 'TEACHER',
  onComplete,
}: Props) {
  const [createStaffMember] = useCreateStaffMember();
  const [kind, setKind] = useState<StaffKind>(defaultKind);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [title, setTitle] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setTitle('');
    setEmployeeId('');
    setKind(defaultKind);
    setError(null);
  }, [defaultKind]);

  const handleSubmit = useCallback(async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await createStaffMember({
        variables: {
          input: {
            schoolId,
            kind,
            personAttributes: {
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: email.trim() || null,
            },
            title: title.trim() || null,
            employeeId: employeeId.trim() || null,
          },
        },
      });
      const userErrors = result.data?.createStaffMember?.userErrors;
      if (userErrors?.length) {
        setError(userErrors.map((e: any) => e.message).join(', '));
        return;
      }
      reset();
      onComplete();
    } catch (e: any) {
      setError(e.message || 'Failed to create staff member');
    } finally {
      setSaving(false);
    }
  }, [firstName, lastName, email, title, employeeId, kind, schoolId, createStaffMember, onComplete, reset]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Staff Member</DialogTitle>
          <DialogDescription>
            Create a person and add them to this school's staff. To add an existing person,
            edit their staff record after creation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="firstName">First name</Label>
              <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="lastName">Last name</Label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>

          <div>
            <Label htmlFor="email">Email (optional)</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          <div>
            <Label>Kind</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as StaffKind)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TEACHER">Teacher</SelectItem>
                <SelectItem value="AIDE">Aide</SelectItem>
                <SelectItem value="ADMIN">Admin</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="title">Title (optional)</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Math Dept Chair"
              />
            </div>
            <div>
              <Label htmlFor="employeeId">Employee ID (optional)</Label>
              <Input id="employeeId" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Adding…' : 'Add Staff Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
